import { useState, useEffect, useCallback } from "react";
import { Routes, Route, NavLink, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Building2, Landmark, Users, Wallet, Boxes, UserSquare2, TrendingUp,
  Megaphone, Workflow, FileText, ShieldCheck, Server, Sparkles, CheckSquare, BarChart3,
  UserCog, ScrollText, Settings, Search, Bell, LogOut, Wifi, WifiOff, Menu, X, ArrowRight,
} from "lucide-react";
import { api } from "@/lib/oraya-api";
import { useAuth } from "@/context/AuthContext";
import { useCommand, useRealtime } from "@/context/CommandContext";
import { OrayaMark } from "@/components/site/primitives";
import { LoadingState, CCButton, StatusBadge } from "@/command/ccUi";

import Overview from "@/command/Overview";
import Company from "@/command/Company";
import Governance from "@/command/Governance";
import Approvals from "@/command/Approvals";
import CCUsers from "@/command/CCUsers";
import Audit from "@/command/Audit";
import People from "@/command/People";
import Finance from "@/command/Finance";
import Products from "@/command/Products";
import Intelligence from "@/command/Intelligence";
import Customers from "@/command/Customers";
import Sales from "@/command/Sales";
import Marketing from "@/command/Marketing";
import Operations from "@/command/Operations";
import Documents from "@/command/Documents";
import Compliance from "@/command/Compliance";
import Contracts from "@/command/Contracts";
import Technology from "@/command/Technology";
import Reports from "@/command/Reports";
import SettingsPage from "@/command/Settings";
import SearchPage from "@/command/Search";
import Placeholder from "@/command/Placeholder";
const NAV = [
  { to: "overview", label: "Overview", icon: LayoutDashboard, mod: "overview" },
  { to: "company", label: "Company", icon: Building2, mod: "company" },
  { to: "governance", label: "Governance", icon: Landmark, mod: "governance" },
  { to: "people", label: "People", icon: Users, mod: "people" },
  { to: "finance", label: "Finance", icon: Wallet, mod: "finance" },
  { to: "products", label: "Products", icon: Boxes, mod: "products" },
  { to: "customers", label: "Customers", icon: UserSquare2, mod: "customers" },
  { to: "sales", label: "Sales", icon: TrendingUp, mod: "sales" },
  { to: "marketing", label: "Marketing", icon: Megaphone, mod: "marketing" },
  { to: "operations", label: "Operations", icon: Workflow, mod: "operations" },
  { to: "documents", label: "Documents", icon: FileText, mod: "documents" },
  { to: "compliance", label: "Compliance", icon: ShieldCheck, mod: "compliance" },
  { to: "contracts", label: "Contracts", icon: FileText, mod: "contracts" },
  { to: "technology", label: "Technology", icon: Server, mod: "technology" },
  { to: "intelligence", label: "Intelligence", icon: Sparkles, mod: "intelligence" },
  { to: "approvals", label: "Approvals", icon: CheckSquare, mod: "approvals" },
  { to: "reports", label: "Reports", icon: BarChart3, mod: "reports" },
  { to: "users", label: "Users & Roles", icon: UserCog, mod: "users" },
  { to: "audit", label: "Audit Log", icon: ScrollText, mod: "audit" },
  { to: "settings", label: "Settings", icon: Settings, mod: "settings" },
];

function Notifications() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/cc/notifications").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useRealtime(["notification", "approvals"], load);
  const unread = items.filter((i) => !i.read).length;

  const markAll = async () => { await api.post("/cc/notifications/read-all"); load(); };

  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} data-testid="notifications-btn" className="relative w-9 h-9 rounded-full glass-medium flex items-center justify-center text-ink hover:border-hairline/25">
        <Bell className="w-4 h-4" />
        {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-oraya-accent text-white text-[10px] flex items-center justify-center px-1" data-testid="notif-count">{unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 glass-heavy rounded-2xl p-2 z-50 max-h-[70vh] overflow-y-auto" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-3 py-2">
            <span className="text-sm font-medium text-ink">Notifications</span>
            <button onClick={markAll} className="text-xs text-subtle hover:text-ink" data-testid="notif-read-all">Mark all read</button>
          </div>
          {items.length === 0 && <p className="text-subtle text-sm px-3 py-6 text-center">No notifications</p>}
          {items.map((n) => (
            <div key={n.id} className={`px-3 py-2.5 rounded-lg ${n.read ? "opacity-60" : "bg-hairline/[0.04]"}`}>
              <p className="text-sm text-ink">{n.text}</p>
              <p className="text-[11px] text-subtle mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NoAccess() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center gap-4 px-6 text-center">
      <OrayaMark size={40} />
      <h1 className="display text-2xl text-ink mt-2">No Command Center access</h1>
      <p className="text-subtle max-w-sm">Your account is authenticated but has no Command Center role. Contact a Super Admin.</p>
      <CCButton variant="ghost" onClick={async () => { await logout(); navigate("/command/login"); }} testid="noaccess-logout">Sign out</CCButton>
    </div>
  );
}

export default function CommandLayout() {
  const { user, loading: authLoading, logout } = useAuth();
  const cmd = useCommand();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNav, setMobileNav] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => { setMobileNav(false); }, [location.pathname]);

  if (authLoading || cmd.loading) {
    return <div data-theme="light" className="min-h-screen bg-surface flex items-center justify-center"><LoadingState label="Loading Command Center…" /></div>;
  }
  if (!user) return <Navigate to="/command/login" replace />;
  if (cmd.error === "forbidden") return <div data-theme="light"><NoAccess /></div>;
  if (!cmd.boot) return <Navigate to="/command/login" replace />;

  const allowed = NAV.filter((n) => (cmd.boot.permissions?.[n.mod] || []).includes("VIEW"));
  const doLogout = async () => { await logout(); navigate("/command/login", { replace: true }); };

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/command/search?q=${encodeURIComponent(q.trim())}`);
  };

  const Sidebar = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 flex items-center gap-2.5 border-b border-hairline/[0.08]">
        <OrayaMark size={24} />
        <div>
          <div className="display text-sm font-medium tracking-[0.18em] text-ink leading-none">ORAYA</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-subtle mt-1">Command Center</div>
        </div>
      </div>
      <nav className="flex-1 p-3 overflow-y-auto flex flex-col gap-0.5">
        {allowed.map((n) => (
          <NavLink key={n.to} to={`/command/${n.to}`} data-testid={`cc-nav-${n.to}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-hairline/[0.07] text-ink" : "text-subtle hover:text-ink hover:bg-hairline/[0.04]"}`}>
            <n.icon className="w-4 h-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-hairline/[0.08]">
        <Link to="/command/intelligence" className="block glass-medium rounded-xl p-3 hover:border-hairline/25 transition-colors" data-testid="cc-ai-command">
          <div className="flex items-center gap-2 text-xs text-subtle"><Sparkles className="w-3.5 h-3.5 text-oraya-accent" /> ORAYA AI COMMAND</div>
          <p className="text-[11px] text-subtle/70 mt-1.5">Ask ORAYA anything about the company — grounded in live data.</p>
        </Link>
      </div>
    </div>
  );

  return (
    <div data-theme="light" className="min-h-screen bg-surface flex text-ink">
      {/* Desktop sidebar */}
      <aside className="w-64 shrink-0 glass-nav border-r border-hairline/[0.08] hidden lg:flex flex-col fixed inset-y-0 z-30">{Sidebar}</aside>

      {/* Mobile drawer */}
      {mobileNav && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNav(false)} />
          <aside className="absolute left-0 inset-y-0 w-72 bg-panel">{Sidebar}</aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-20 glass-nav border-b border-hairline/[0.08] px-4 md:px-6 py-3 flex items-center gap-3">
          <button className="lg:hidden text-ink" onClick={() => setMobileNav(true)} data-testid="cc-mobile-nav"><Menu className="w-5 h-5" /></button>
          <form onSubmit={onSearch} className="flex-1 max-w-md relative">
            <Search className="w-4 h-4 text-subtle absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ORAYA…" data-testid="global-search"
              className="w-full glass-medium rounded-full pl-9 pr-4 py-2 text-sm text-ink placeholder:text-subtle/60 focus:outline-none focus:border-hairline/25" />
          </form>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden md:flex items-center gap-1.5 text-xs text-subtle" title={cmd.connected ? "Realtime connected" : "Reconnecting…"} data-testid="rt-status">
              {cmd.connected ? <Wifi className="w-3.5 h-3.5 text-emerald-500" /> : <WifiOff className="w-3.5 h-3.5 text-amber-500" />}
              <span className="hidden lg:inline">{cmd.connected ? "Live" : "Reconnecting"}</span>
            </span>
            <Notifications />
            <div className="flex items-center gap-2 pl-2 border-l border-hairline/10">
              <div className="text-right hidden sm:block">
                <div className="text-sm text-ink leading-none">{cmd.boot.user.name || cmd.boot.user.email}</div>
                <div className="text-[11px] text-subtle mt-0.5">{cmd.boot.user.roles.join(", ")}</div>
              </div>
              <button onClick={doLogout} data-testid="cc-logout" className="w-9 h-9 rounded-full glass-medium flex items-center justify-center text-ink hover:border-hairline/25"><LogOut className="w-4 h-4" /></button>
            </div>
          </div>
        </header>

        <main className="p-4 md:p-8 max-w-[1500px]">
          <Routes>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<Overview />} />
            <Route path="company" element={<Company />} />
            <Route path="governance" element={<Governance />} />
            <Route path="people" element={<People />} />
            <Route path="finance" element={<Finance />} />
            <Route path="products" element={<Products />} />
            <Route path="intelligence" element={<Intelligence />} />
            <Route path="customers" element={<Customers />} />
            <Route path="sales" element={<Sales />} />
            <Route path="marketing" element={<Marketing />} />
            <Route path="operations" element={<Operations />} />
            <Route path="documents" element={<Documents />} />
            <Route path="compliance" element={<Compliance />} />
            <Route path="contracts" element={<Contracts />} />
            <Route path="technology" element={<Technology />} />
            <Route path="reports" element={<Reports />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="users" element={<CCUsers />} />
            <Route path="audit" element={<Audit />} />
            <Route path="enquiries" element={<Placeholder title="Enquiries" hint="Contact enquiries are managed in the admin CMS. Deep integration arrives in a later phase." />} />
            <Route path="search" element={<SearchPage />} />
            <Route path="*" element={<Placeholder title="Not found" hint="This module doesn't exist." />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
